import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const DEFAULT_FALLBACK = 'Failed to create cluster.';

function extractMessage(data: unknown): string | undefined {
  if (data == null) return undefined;
  if (typeof data === 'string') return data;
  if (typeof data !== 'object') return undefined;

  const record = data as Record<string, unknown>;
  if (typeof record.message === 'string') return record.message;
  if (typeof record.error === 'string') return record.error;
  if (Array.isArray(record.errors) && typeof record.errors[0] === 'string') {
    return record.errors[0];
  }
  return undefined;
}

function getCreateErrorMessageFromStatus(
  status: number,
  fetchError: FetchBaseQueryError,
): string | null {
  if (status === 403) return 'Only super users can create clusters.';
  if (status === 409) {
    return (
      extractMessage(fetchError.data) ??
      'Cluster name is already in use or there is a conflict with the default cluster.'
    );
  }
  if (status === 400) return extractMessage(fetchError.data) ?? 'Invalid cluster data.';
  return null;
}

/**
 * User-facing message for cluster create failures (403 / 400 / 409 and body text).
 */
export function getCreateClusterErrorMessage(
  error: unknown,
  fallbackMessage: string = DEFAULT_FALLBACK,
): string {
  if (error == null || typeof error !== 'object') return fallbackMessage;

  const fetchError = error as FetchBaseQueryError;
  const { status, data } = fetchError;

  if (status === 'FETCH_ERROR' || status === 'PARSING_ERROR' || status === 'TIMEOUT_ERROR') {
    return fallbackMessage;
  }

  if (typeof status === 'number') {
    const message = getCreateErrorMessageFromStatus(status, fetchError);
    if (message) return message;
  }

  return extractMessage(data) ?? fallbackMessage;
}

const DELETE_CLUSTER_FALLBACK = 'Failed to delete cluster.';

function getDeleteErrorMessageFromStatus(
  status: number,
  fetchError: FetchBaseQueryError,
): string | null {
  if (status === 403) return 'Only super users can delete clusters.';
  if (status === 404) return extractMessage(fetchError.data) ?? 'Cluster not found.';
  return null;
}

/**
 * User-facing message for cluster delete failures (403 / 404 and body text).
 */
export function getDeleteClusterErrorMessage(
  error: unknown,
  fallbackMessage: string = DELETE_CLUSTER_FALLBACK,
): string {
  if (error == null || typeof error !== 'object') return fallbackMessage;

  const fetchError = error as FetchBaseQueryError;
  const { status, data } = fetchError;

  if (status === 'FETCH_ERROR' || status === 'PARSING_ERROR' || status === 'TIMEOUT_ERROR') {
    return fallbackMessage;
  }

  if (typeof status === 'number') {
    const message = getDeleteErrorMessageFromStatus(status, fetchError);
    if (message) return message;
  }

  return extractMessage(data) ?? fallbackMessage;
}
